import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Read CSRF cookie value (double-submit pattern)
export function getCsrfToken() {
  const match = document.cookie.match(/(?:^|;\s*)vfm_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : "";
}

const client = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Attach CSRF header on state-changing requests
client.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const token = getCsrfToken();
    if (token) config.headers["X-CSRF-Token"] = token;
  }
  return config;
});

// Session refresh. One refresh at a time per tab (shared promise); the
// server tolerates concurrent refreshes from other tabs (grace window).
// Outcomes: "ok" | "invalid" (definitive 401 → login needed) | "transient"
// (network/timeout/5xx → keep auth state, retry later).
let refreshPromise = null;
let authEpoch = 0;  // bumped on logout so stale refreshes/retries are ignored
const NO_REFRESH_PATHS = ["/auth/login", "/auth/refresh", "/auth/activate",
  "/auth/logout", "/auth/forgot-password", "/auth/bootstrap-admin"];

export const isTransientError = (err) => {
  const s = err?.response?.status;
  return !err?.response || s === 408 || s === 429 || s >= 500;
};

function broadcastSessionLost() {
  try {
    window.dispatchEvent(new CustomEvent("auth:session-lost"));
  } catch { /* non-browser env */ }
}

export function refreshSession() {
  if (refreshPromise) return refreshPromise;
  const epoch = authEpoch;
  refreshPromise = client.post("/auth/refresh", null, { timeout: 15000 })
    .then(() => (epoch === authEpoch ? "ok" : "invalid"))
    .catch((e) => (e?.response?.status === 401 ? "invalid" : "transient"))
    .then((result) => {
      if (result === "invalid" && epoch === authEpoch) broadcastSessionLost();
      return result;
    })
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
}

// Auth generation: any account-state response started under an older
// epoch must be discarded (logout, account switch, cross-tab logout).
export const getAuthEpoch = () => authEpoch;
export function bumpAuthEpoch() { authEpoch += 1; return authEpoch; }

export async function beginLogout() {
  bumpAuthEpoch();
  if (refreshPromise) await refreshPromise.catch(() => {});
}

client.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const url = original.url || "";
    // Only 401 triggers refresh; 403 (permission/CSRF) is never a logout.
    // A 401 is rejected by the auth dependency before any business logic
    // runs, so retrying the original request once cannot duplicate it.
    if (error.response?.status !== 401 || original._retry ||
        NO_REFRESH_PATHS.some((p) => url.includes(p))) {
      return Promise.reject(error);
    }
    original._retry = true;
    const epoch = authEpoch;
    const result = await refreshSession();
    if (result === "ok" && epoch === authEpoch) return client(original);
    if (result === "transient") error.vfmTransient = true;
    return Promise.reject(error);
  }
);

export const apiClient = client;
export { client };

export const authApi = {
  login: (username, password) =>
    client.post("/auth/login", { username, password }).then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  activate: (token, newPin) =>
    client.post("/auth/activate", { token, newPin }).then((r) => r.data),
  forgot: (username) =>
    client.post("/auth/forgot-password", { username }).then((r) => r.data),
  setPin: (currentCredential, newPin) =>
    client.post("/auth/set-pin", { currentCredential, newPin }).then((r) => r.data),
};

export const jobsApi = {
  list: () => client.get("/jobs").then((r) => r.data),
  listTrashed: () => client.get("/jobs?trashed=true").then((r) => r.data),
  listForCustomer: (customerId, excludeId) =>
    client.get(`/jobs?customerId=${encodeURIComponent(customerId)}${excludeId ? `&excludeId=${encodeURIComponent(excludeId)}` : ""}`).then((r) => r.data),
  stats: () => client.get("/jobs/stats").then((r) => r.data),
  get: (id) => client.get(`/jobs/${id}`).then((r) => r.data),
  create: (data) => client.post("/jobs", data).then((r) => r.data),
  update: (id, data) => client.put(`/jobs/${id}`, data).then((r) => r.data),
  trash: (id) => client.delete(`/jobs/${id}`).then((r) => r.data),
  restore: (id) => client.post(`/jobs/${id}/restore`).then((r) => r.data),
  deletePermanent: (id) => client.delete(`/jobs/${id}/permanent`).then((r) => r.data),
  history: (id) => client.get(`/jobs/${id}/history`).then((r) => r.data),
  linkCustomer: (id, body) => client.post(`/jobs/${id}/link-customer`, body).then((r) => r.data),
};

export const customersApi = {
  search: (q) => client.get(`/customers/search?q=${encodeURIComponent(q)}`).then((r) => r.data),
};

export const usersApi = {
  list: () => client.get("/users").then((r) => r.data),
  create: (data) => client.post("/users", data).then((r) => r.data),
  update: (id, data) => client.patch(`/users/${id}`, data).then((r) => r.data),
  resetPassword: (id) => client.post(`/users/${id}/reset-password`).then((r) => r.data),
  remove: (id) => client.delete(`/users/${id}`).then((r) => r.data),
};

export const techniciansApi = {
  list: () => client.get("/technicians").then((r) => r.data),
  add: (name) => client.post("/technicians", { name }).then((r) => r.data),
  remove: (name) =>
    client.delete(`/technicians/${encodeURIComponent(name)}`).then((r) => r.data),
};

export const SE_PHONE_RE = /^(?:\+46|0046|0)\s*7[\s\-]?\d(?:[\s\-]?\d){7}$/;
export const isValidSEPhone = (v) => SE_PHONE_RE.test((v || "").trim());

export const STATUSES = ["Mottagen", "Beställd", "Väntar på kund", "Levererad", "Avbokad"];
export const STORES = ["ValfriMobil Mölndal", "ValfriMobil Alingsås"];
export const DEFAULT_STORE = "ValfriMobil Mölndal";

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Något gick fel.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  }
  if (detail?.msg) return detail.msg;
  return String(detail);
}
